import type { VerificationStatus } from "@/domain";
import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";

/**
 * Utility function to merge Tailwind classes with proper conflict resolution
 */
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Layout & Container Utilities
 */
export const layout = {
  // Main container layouts
  container: "flex flex-1 flex-col",
  containerMain: "@container/main flex flex-1 flex-col gap-2",
  containerCard: "@container/card",

  // Flex layouts
  flex: "flex",
  flexCol: "flex flex-col",
  flexRow: "flex flex-row",
  flexCenter: "flex items-center justify-center",
  flexBetween: "flex items-center justify-between",
  flexStart: "flex items-center justify-start",
  flexEnd: "flex items-center justify-end",
  flexWrap: "flex flex-wrap",
  flexNowrap: "flex flex-nowrap",
  flex1: "flex-1",
  justifyCenter: "justify-center",

  // Grid layouts
  grid: "grid",
  gridCols1: "grid-cols-1",
  gridCols2: "grid-cols-2",
  gridCols3: "grid-cols-3",
  gridCols4: "grid-cols-4",
  gridResponsive: "grid-cols-1 @xl/main:grid-cols-2 @5xl/main:grid-cols-4",
  gridGap1_5: "grid gap-1.5",
  gridCols1MdCols2: "grid grid-cols-1 md:grid-cols-2 gap-4",
  gridCols1LgCols2: "grid grid-cols-1 lg:grid-cols-2 gap-6",

  // Full width/height
  fullWidth: "w-full",
  fullHeight: "h-full",
  minWidth0: "min-w-0",
  minHScreen: "min-h-screen",

  // Responsive containers
  responsiveContainer: "px-4 lg:px-6",
  responsiveGap: "gap-1 lg:gap-2",

  // Common flex patterns
  flexColGapHalf: "flex flex-col gap-0.5",
  flexColGap1: "flex flex-col gap-1",
  flexColGap2: "flex flex-col gap-2",
  flexColGap3: "flex flex-col gap-3",
  flexColGap4: "flex flex-col gap-4",
  flexColGap6: "flex flex-col gap-6",
  flexRowGapHalf: "flex flex-row gap-0.5",
  flexRowGap1: "flex flex-row gap-1",
  flexRowGap2: "flex flex-row gap-2",
  flexRowGap3: "flex flex-row gap-3",
  flexRowGap4: "flex flex-row gap-4",
  flexRowGap6: "flex flex-row gap-6",
  flexRowGap2ColSpan1: "flex flex-row gap-2 col-span-1",
  flexCenterGap1: "flex items-center gap-1",
  flexCenterGap1_5: "flex items-center gap-1.5",
  flexCenterGap2: "flex items-center gap-2",
  flexCenterGap3: "flex items-center gap-3",
  flexCenterGap4: "flex items-center gap-4",
  flexBetweenGap2: "flex items-center justify-between gap-2",
  itemsEnd: "items-end",
  itemsCenter: "items-center",
  flexStartGap2: "flex items-center gap-2",
  flexEndGap2: "flex items-center justify-end gap-2",
  flexColSmFlexRow: "flex-col sm:flex-row",

  flexItemsStartGap2: "flex items-start gap-2",
  flexItemsStartGap3: "flex items-start gap-3",

  // Common wrapper patterns
  wrapper: "flex h-full w-full flex-col",
  wrapperCenter: "flex h-full w-full items-center justify-center",
  wrapperBetween: "flex h-full w-full items-center justify-between",

  // Common container patterns
  containerP2: "flex flex-col gap-2 p-2",
  containerP4: "flex flex-col gap-2 p-4",
  containerRelative: "relative flex w-full min-w-0 flex-col",
  containerMxAuto: "container mx-auto",
  mxAuto: "mx-auto",

  // Image selector patterns
  imageSelectorContainer: "w-full flex flex-col gap-4",
  imageSelectorUploadArea:
    "flex flex-col flex-1 border-2 border-dashed rounded-lg p-4 transition-colors",
  imageSelectorBorderActive: "border-primary bg-primary/5",
  imageSelectorBorderDefault: "border-border hover:border-input",
  imageSelectorBorderSolid: "border-solid",
  imageSelectorDisabled: "opacity-50 cursor-not-allowed",
  imageSelectorPreview: "object-cover rounded-lg",
  imageSelectorButtonContainer: "flex gap-2",
  imageSelectorButton: "flex-1 text-xs",
  imageSelectorButtonRemove: "flex-1 text-xs text-destructive hover:text-destructive/80",
  imageSelectorUploadContent:
    "text-center space-y-3 flex flex-col flex-1 items-center justify-center",
  imageSelectorTextContainer:
    "text-sm text-muted-foreground flex flex-col items-center justify-center",
  imageSelectorTextMedium: "font-medium",
  imageSelectorTextSmall: "text-xs text-muted-foreground",
  imageSelectorTextMuted: "text-xs text-muted-foreground/70 mt-1"
} as const;

/**
 * Spacing Utilities
 */
export const spacing = {
  // Gaps
  gap1: "gap-1",
  gap2: "gap-2",
  gap3: "gap-3",
  gap4: "gap-4",
  gap6: "gap-6",
  gap8: "gap-8",
  gap1_5: "gap-1.5",

  // Padding
  p2: "p-2",
  p3: "p-3",
  p4: "p-4",
  p6: "p-6",
  p8: "p-8",
  px0: "px-0",
  px1: "px-1",
  px2: "px-2",
  px4: "px-4",
  px6: "px-6",
  px8: "px-8",
  py1: "py-1",
  py2: "py-2",
  py4: "py-4",
  py6: "py-6",
  py8: "py-8",
  py12: "py-12",
  py16: "py-16",
  py1_5: "py-1.5",
  pt2: "pt-2",
  pt4: "pt-4",
  pb2: "pb-2",
  pb4: "pb-4",
  pb8: "pb-8",

  // Margin
  m2: "m-2",
  m4: "m-4",
  mx1: "mx-1",
  mx2: "mx-2",
  mx4: "mx-4",
  mxAuto: "mx-auto",
  my1: "my-1",
  my2: "my-2",
  my4: "my-4",
  my6: "my-6",
  my8: "my-8",
  mlAuto: "ml-auto",
  mrAuto: "mr-auto",
  mr2: "mr-2",
  mtAuto: "mt-auto",
  mt1: "mt-1",
  mt2: "mt-2",
  mt3: "mt-3",
  mt4: "mt-4",
  mt5: "mt-5",
  mt6: "mt-6",
  mt7: "mt-7",
  mt8: "mt-8",
  mbAuto: "mb-auto",
  mb2: "mb-2",
  mb3: "mb-3",
  mb4: "mb-4",
  mb6: "mb-6",
  mb8: "mb-8",
  mr1: "mr-1",
  mb1: "mb-1",

  // Negative margins
  negMl1: "-ml-1",
  negMx1: "-mx-1",

  // Common spacing patterns
  p4LgP6: "px-4 lg:px-6",
  py4MdPy6: "py-4 md:py-6",
  gap4MdGap6: "gap-4 md:gap-6",
  mtAutoP4: "mt-auto flex flex-col gap-2 p-4"
} as const;

/**
 * Typography Utilities
 */
export const typography = {
  // Text sizes
  textXs: "text-xs",
  textSm: "text-sm",
  textBase: "text-base",
  textMd: "text-md",
  textLg: "text-lg",
  textXl: "text-xl",
  text2xl: "text-2xl",
  text3xl: "text-3xl",
  text4xl: "text-4xl",
  text6xl: "text-6xl",

  // Font weights
  fontMedium: "font-medium",
  fontSemibold: "font-semibold",
  fontBold: "font-bold",
  fontNormal: "font-normal",
  fontMono: "font-mono",

  // Text colors
  textForeground: "text-foreground",
  textPrimary: "text-primary",
  textSecondary: "text-secondary-foreground",
  textBlue600: "text-blue-600",
  textBlue700: "text-blue-700",
  textGreen600: "text-green-600",
  textRed600: "text-red-600",
  textOrange600: "text-orange-600",
  textYellow500: "text-yellow-500",
  textYellow600: "text-yellow-600",
  textPurple600: "text-purple-600",
  textGold600: "text-yellow-600",
  textGray: "text-muted-foreground",

  // Text alignment
  textLeft: "text-left",
  textCenter: "text-center",
  textRight: "text-right",
  textJustify: "text-justify",

  // Text utilities
  trackingTight: "tracking-tight",
  tabularNums: "tabular-nums",
  lineClamp1: "line-clamp-1",
  lineClamp2: "line-clamp-2",
  lineClamp3: "line-clamp-3",
  leadingTight: "leading-tight",
  leadingNone: "leading-none",

  // Error message styles
  errorMessage: "text-xs text-destructive mt-1",
  phoneErrorMessage: "text-xs text-destructive mt-1",

  // Card sub header styles
  cardSubHeader: "border-b border-border pb-2",
  cardSubHeaderTitle:
    "text-sm font-medium text-muted-foreground uppercase tracking-wide",

  // Responsive text
  responsiveText: "@[250px]/card:text-3xl",

  // Common text patterns
  text3xlBold: "text-3xl font-bold tracking-tight",
  text2xlBold: "text-2xl font-bold tracking-tight",
  text2xlSemibold: "text-2xl font-semibold tabular-nums",
  textBaseMedium: "text-base font-medium",
  textSmMuted: "text-sm text-muted-foreground",
  textXsMuted: "text-xs text-muted-foreground",
  textMuted: "text-muted-foreground",
  textForegroundSemibold: "text-foreground font-semibold",
  textSidebarForeground70: "text-sidebar-foreground/70",
  text4xlBold: "text-4xl font-bold",
  text6xlBold: "text-6xl font-bold",
  textXlSemibold: "text-xl font-semibold",
  textLgSemibold: "text-lg font-semibold",
  textMdSemibold: "text-md font-semibold",

  textLgBold: "text-lg font-bold",
  text4xlMdText6xl: "text-4xl md:text-6xl",
  srOnly: "sr-only",

  textDisplayBold24:
    "font-bold text-2xl leading-9 tracking-normal font-family-sans",
  textDisplayBold28:
    "font-bold text-3xl leading-9 tracking-normal font-family-sans",
  textBodyBold16:
    "font-bold !text-base font-family-sans leading-6 tracking-normal",
  textBodyMedium18: "font-medium text-lg font-family-sans ",
  textBodyMeduim16:
    "font-medium !text-base font-family-sans leading-6 tracking-normal",
  textBodySmall16:
    "font-normal text-xs font-family-sans text-foreground/60 dark:text-muted-foreground",
  textSmallMedium16: "font-medium text-sm font-family-sans",
  textSmallMedium14:
    "font-medium text-sm font-family-sans,  tracking-normal leading-5",
  textCaptionMedium12: "font-medium text-xs font-family-sans ",
  textCaptionMedium14:
    "font-medium text-[14px] font-family-sans text-muted-foreground",
  textTitleBold26: "font-bold text-[26px] font-family-sans ",
  textTitleBold24: "font-bold text-[24px] font-family-sans ",
  textButtonMeduim16: "font-medium !text-base font-family-sans  uppercase",
  textButtonMeduim18: "font-medium !text-lg font-family-sans  uppercase",
  textHeaderTitleMedium18: "font-medium !text-xl font-family-sans ",
  textHeaderTitleMedium20:
    "font-medium !text-lg font-family-sans  leading-6.5 tracking-normal",
  textHeaderTitleBold20:
    "font-bold !text-xl leading-7 tracking-normal font-family-sans ",
  textSmallBold14:
    "font-bold text-sm font-family-sans,  leading-5 tracking-normal",
  textSmallBold12:
    "font-bold text-xs font-family-sans,  leading-5 tracking-normal",
  textSmallRegular12:
    "font-normal text-xs font-family-sans,  leading-5 tracking-normal",
  textSmallRegular14:
    "font-normal text-sm font-family-sans,  leading-5 tracking-normal",
  textHeadinglBold20:
    "font-bold text-xl font-family-sans,  leading-7 tracking-normal",
  textCardTitleBold18:
    "font-bold text-lg font-family-sans  tracking-normal leading-6.5"
} as const;

/**
 * Scrollbar Utilities
 */
export const scrollbar = {
  // Base scrollbar styles
  thin: "scrollbar-thin",
  none: "scrollbar-none",

  // Enhanced theme-based scrollbar colors for sidebar
  sidebar:
    "scrollbar-thin scrollbar-track-transparent scrollbar-thumb-sidebar-border/60 hover:scrollbar-thumb-sidebar-ring/80",
  sidebarDark:
    "scrollbar-thin scrollbar-track-transparent scrollbar-thumb-sidebar-border/70 hover:scrollbar-thumb-sidebar-ring/90 dark:scrollbar-thumb-sidebar-border/70 dark:hover:scrollbar-thumb-sidebar-ring/90",

  // Generic theme scrollbar
  theme:
    "scrollbar-thin scrollbar-track-transparent scrollbar-thumb-border/60 hover:scrollbar-thumb-ring/80",
  themeDark:
    "scrollbar-thin scrollbar-track-transparent scrollbar-thumb-border/70 hover:scrollbar-thumb-ring/90 dark:scrollbar-thumb-border/70 dark:hover:scrollbar-thumb-ring/90",

  // Smooth scrolling
  smooth: "scroll-smooth",

  // Mobile optimized
  mobile:
    "scrollbar-thin scrollbar-track-transparent scrollbar-thumb-sidebar-border/70 hover:scrollbar-thumb-sidebar-ring/80",

  // Hidden scrollbar (for collapsed states)
  hidden: "scrollbar-none",

  // Custom scrollbar with specific colors
  custom:
    "scrollbar-thin scrollbar-track-transparent scrollbar-thumb-muted-foreground/20 hover:scrollbar-thumb-muted-foreground/40",

  // Enhanced scrollbar variants
  subtle:
    "scrollbar-thin scrollbar-track-transparent scrollbar-thumb-muted-foreground/30 hover:scrollbar-thumb-muted-foreground/50",
  prominent:
    "scrollbar-thin scrollbar-track-transparent scrollbar-thumb-foreground/40 hover:scrollbar-thumb-foreground/60",

  // Size variants
  small: "scrollbar-thin",
  medium: "scrollbar-thin",
  large: "scrollbar-thin",

  // Color variants
  primary:
    "scrollbar-thin scrollbar-track-transparent scrollbar-thumb-primary/40 hover:scrollbar-thumb-primary/60",
  secondary:
    "scrollbar-thin scrollbar-track-transparent scrollbar-thumb-secondary-foreground/40 hover:scrollbar-thumb-secondary-foreground/60",
  accent:
    "scrollbar-thin scrollbar-track-transparent scrollbar-thumb-accent-foreground/40 hover:scrollbar-thumb-accent-foreground/60",

  // Accessibility focused
  accessible:
    "scrollbar-thin scrollbar-track-transparent scrollbar-thumb-foreground/60 hover:scrollbar-thumb-foreground/80",

  // High contrast mode
  highContrast:
    "scrollbar-thin scrollbar-track-transparent scrollbar-thumb-foreground/80 hover:scrollbar-thumb-foreground",

  // Reduced motion support
  reducedMotion: "scroll-auto",

  // Print friendly
  print: "scrollbar-none",

  // Overflow control
  overflowHidden: "overflow-hidden",
  overflowAuto: "overflow-auto",
  overflowScroll: "overflow-scroll",
  overflowXHidden: "overflow-x-hidden",
  overflowYAuto: "overflow-y-auto"
} as const;

/**
 * Border & Background Utilities
 */
export const borders = {
  // Border width
  border: "border",
  border0: "border-0",
  border2: "border-2",
  border4: "border-4",

  // Border sides
  borderT: "border-t",
  borderB: "border-b",
  borderL: "border-l",
  borderR: "border-r",
  borderL4: "border-l-4",

  // Border colors
  borderBlue500: "border-blue-500",
  borderGreen500: "border-green-500",
  borderGreen600: "border-green-600",
  borderPurple500: "border-purple-500",
  borderOrange500: "border-orange-500",
  borderSidebar: "border-sidebar-border",
  borderTransparent: "border-transparent",
  borderPrimary: "border-primary",
  borderMuted: "border-muted-foreground",

  // Border radius
  rounded: "rounded",
  roundedMd: "rounded-md",
  roundedLg: "rounded-lg",
  roundedFull: "rounded-full",

  // Border patterns
  borderL4Blue: "border-l-4 border-l-blue-500",
  borderL4Green: "border-l-4 border-l-green-500",
  borderL4Purple: "border-l-4 border-l-purple-500",
  borderL4Orange: "border-l-4 border-l-orange-500"
} as const;

/**
 * Background Utilities
 */
export const backgrounds = {
  // Background colors
  bgBackground: "bg-background",
  bgCard: "bg-card",
  bgPrimary: "bg-primary",
  bgSecondary: "bg-secondary",
  bgMuted: "bg-muted",
  bgMuted50: "bg-muted/50",
  bgAccent: "bg-accent",
  bgSidebar: "bg-sidebar",
  bgDestructive: "bg-destructive",
  bgTransparent: "bg-transparent",
  bgBlack50: "bg-black/50",
  bgPrimary10: "bg-primary/10",
  bgBlue100: "bg-blue-100",
  bgGreen100: "bg-green-100",
  bgRed100: "bg-red-100",
  bgOrange100: "bg-orange-100",
  bgYellow50: "bg-yellow-50",
  bgYellow100: "bg-yellow-100",
  bgRed50: "bg-red-50",
  bgPurple100: "bg-purple-100",
  bgGold100: "bg-yellow-100",

  // Background utilities
  bgGradient: "bg-gradient-to-t",
  bgGradientPrimary: "from-primary/5 to-card"
} as const;

/**
 * Size Utilities
 */
export const sizes = {
  // Height
  hFull: "h-full",
  h8: "h-8",
  h16: "h-16",
  h24: "h-24",
  h32: "h-32",
  h64: "h-64",
  h4: "h-4",
  hPx: "h-px",
  h2: "h-2",
  h9: "h-9",

  // Width
  wFull: "w-full",
  wAuto: "w-auto",
  w8: "w-8",
  w16: "w-16",
  w24: "w-24",
  w32: "w-32",
  w38: "w-38",
  w48: "w-48",
  w56: "w-56",

  // Max width
  maxWxl: "max-w-xl",
  maxW2xl: "max-w-2xl",
  maxW3xl: "max-w-2xl",
  maxW4xl: "max-w-4xl",

  // Size (width + height)
  size4: "size-4",
  size5: "size-5",
  size6: "size-6",
  size7: "size-7",
  size8: "size-8",
  size9: "size-9",
  size10: "size-10",
  size12: "size-12",
  size3_5: "size-3.5",
  size2_5: "size-2.5",
  size3: "size-3",

  // Custom sizes
  headerHeight: "h-(--header-height)",
  sidebarWidth: "w-(--sidebar-width)",
  skeletonWidth: "max-w-(--skeleton-width)",

  // Common size patterns
  size4Opacity50: "size-4 opacity-50",
  size4RoundedMd: "size-4 rounded-md",
  size7HoverTransparent: "size-7 hover:bg-transparent",
  size3Muted: "size-3 text-muted-foreground"
} as const;

/**
 * Tier Icon Sizes
 */
export const tierIconSizes = {
  sm: "px-2 py-1 text-xs",
  md: "px-3 py-1.5 text-sm",
  lg: "px-4 py-2 text-base"
} as const;

/**
 * Position & Display Utilities
 */
export const position = {
  // Position
  relative: "relative",
  absolute: "absolute",
  fixed: "fixed",
  sticky: "sticky",

  // Positioning
  top4: "top-4",
  top2: "top-2",
  right4: "right-4",
  left2: "left-2",
  right2: "right-2",
  inset0: "inset-0",
  insetY0: "inset-y-0",
  insetX0: "inset-x-0",

  // Display
  hidden: "hidden",
  block: "block",
  inline: "inline",
  inlineBlock: "inline-block",

  // Responsive display
  hiddenSm: "hidden sm:flex",
  hiddenMd: "hidden md:block",
  mdHidden: "md:hidden",
  hidden540: "hidden @[540px]/card:block",
  block540Hidden: "@[540px]/card:hidden",

  // Z-index
  z0: "z-0",
  z10: "z-10",
  z50: "z-50"
} as const;

/**
 * Interactive Utilities
 */
export const interactive = {
  // Cursor
  cursorPointer: "cursor-pointer",
  pointerEventsNone: "pointer-events-none",

  // Focus
  focusRing: "focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px]",
  focusOutline: "focus:outline-none",
  focusVisible:
    "outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px]",

  // Hover
  hoverOpacity: "hover:opacity-100",
  hoverBg: "hover:bg-accent",
  hoverBgTransparent: "hover:bg-transparent",
  hoverTextForeground: "hover:text-foreground",

  // Active
  activeBg: "active:bg-accent",
  activeBgPrimary: "active:bg-primary/90",

  // Disabled
  disabled: "disabled:opacity-50 disabled:pointer-events-none",
  disabledCursor: "disabled:cursor-not-allowed",

  // State
  dataOpenBg: "data-[state=open]:bg-accent",
  dataOpenText: "data-[state=open]:text-accent-foreground",
  dataSelectedBg: "data-[state=selected]:bg-muted"
} as const;

/**
 * Animation & Transition Utilities
 */
export const animation = {
  // Transitions
  transition: "transition-all",
  transitionOpacity: "transition-opacity",
  transitionWidth: "transition-[width,height]",
  transitionEase: "ease-linear",
  transitionColors: "transition-colors",

  // Animations
  animatePulse: "animate-pulse",
  animateIn: "data-[state=open]:animate-in",
  animateOut: "data-[state=closed]:animate-out",
  fadeIn: "data-[state=open]:fade-in-0",
  fadeOut: "data-[state=closed]:fade-out-0",

  // Transform
  scale100: "scale-100",
  scale0: "scale-0",
  rotate0: "rotate-0",
  rotate90: "rotate-90",
  rotateNeg90: "-rotate-90",

  // Dark mode transforms
  darkScale0: "dark:scale-0",
  darkScale100: "dark:scale-100",
  darkRotateNeg90: "dark:-rotate-90",
  darkRotate0: "dark:rotate-0",

  // Duration
  duration200: "duration-200",
  duration300: "duration-300",
  duration500: "duration-500"
} as const;

/**
 * Shadow Utilities
 */
export const shadows = {
  shadowNone: "shadow-none",
  shadowXs: "shadow-xs",
  shadowSm: "shadow-sm",
  shadowLg: "shadow-lg",
  dropShadow: "shadow-[0px_2px_10px_0px_rgba(0,0,0,0.1)]"
} as const;

/**
 * Opacity Utilities
 */
export const opacity = {
  opacity50: "opacity-50",
  opacity70: "opacity-70",
  opacity80: "opacity-80"
} as const;

/**
 * Overflow Utilities
 */
export const overflow = {
  overflowXAuto: "overflow-x-auto",
  overflowHidden: "overflow-hidden"
} as const;

/**
 * Responsive Utilities
 */
export const responsive = {
  // Grid responsive
  gridCols1MdGridCols2: "grid-cols-1 md:grid-cols-2",
  gridCols1MdGridCols3: "grid-cols-1 md:grid-cols-3",
  gridCols1MdGridCols2LgGridCols3: "grid-cols-1 md:grid-cols-2 lg:grid-cols-3",
  gridCols1MdGridCols2LgGridCols4: "grid-cols-1 md:grid-cols-2 lg:grid-cols-4",
  gridCols1LgGridCols2: "grid-cols-1 lg:grid-cols-2",
  gridCols2: "grid-cols-2",
  gridCols7: "grid-cols-7",

  // Flex responsive
  flexColSmFlexRow: "flex-col sm:flex-row",
  flexWrap: "flex-wrap",

  // Text responsive
  text4xlMdText6xl: "text-4xl md:text-6xl",

  // Spacing responsive
  gap4SmGap4: "gap-4 sm:gap-4",
  px4LgPx6: "px-4 lg:px-6",
  py4MdPy6: "py-4 md:py-6"
} as const;

/**
 * Verification Banner Utilities
 */

export interface VerificationStatusConfig {
  title: string;
  description: string;
  theme: string;
  action: string;
  tooltip: string;
}

export interface VerificationThemeClasses {
  container: string;
  icon: string;
  iconBg: string;
  title: string;
  description: string;
  hover: string;
}

export const verification = {
  getStatusConfig: (
    status: VerificationStatus
  ): VerificationStatusConfig | null => {
    switch (status) {
      case "pending":
        return {
          title: "Verification Pending",
          description: "Upload documents to access all features",
          theme: "error",
          action: "Upload Documents",
          tooltip: "Click to upload verification documents"
        };
      case "under_review":
      case "resubmitted":
        return {
          title: "Under Review",
          description:
            "Documents being reviewed. You'll be notified when approved",
          theme: "warning",
          action: "View Status",
          tooltip: "Click to view verification status"
        };
      case "rejected":
        return {
          title: "Verification Failed",
          description:
            "Documents couldn't be verified. Please check and resubmit",
          theme: "error",
          action: "Resubmit Documents",
          tooltip: "Click to resubmit verification documents"
        };
      case "approved":
        return {
          title: "Verified!",
          description:
            "You can now apply for shifts and connect with practices",
          theme: "success",
          action: "View Status",
          tooltip: "Click to view verification status"
        };
      default:
        return null;
    }
  },

  getThemeClasses: (theme: string): VerificationThemeClasses | null => {
    switch (theme) {
      case "error":
        return {
          container:
            "border-red-200 bg-red-50/80 dark:border-red-800/50 dark:bg-red-950/20",
          icon: "text-red-600 dark:text-red-400",
          iconBg: "bg-red-100 dark:bg-red-900/30",
          title: "text-red-900 dark:text-red-100",
          description: "text-red-700 dark:text-red-300",
          hover: "hover:bg-red-100/80 dark:hover:bg-red-900/30"
        };
      case "warning":
        return {
          container:
            "border-amber-200 bg-amber-50/80 dark:border-amber-800/50 dark:bg-amber-950/20",
          icon: "text-amber-600 dark:text-amber-400",
          iconBg: "bg-amber-100 dark:bg-amber-900/30",
          title: "text-amber-900 dark:text-amber-100",
          description: "text-amber-700 dark:text-amber-300",
          hover: "hover:bg-amber-100/80 dark:hover:bg-amber-900/30"
        };
      case "success":
        return {
          container:
            "border-green-200 bg-green-50/80 dark:border-green-800/50 dark:bg-green-950/20",
          icon: "text-green-600 dark:text-green-400",
          iconBg: "bg-green-100 dark:bg-green-900/30",
          title: "text-green-900 dark:text-green-100",
          description: "text-green-700 dark:text-green-300",
          hover: "hover:bg-green-100/80 dark:hover:bg-green-900/30"
        };
      default:
        return {
          container:
            "border-border bg-muted/50 dark:border-border dark:bg-muted/20",
          icon: "text-muted-foreground",
          iconBg: "bg-muted dark:bg-muted/30",
          title: "text-foreground",
          description: "text-foreground/80",
          hover: "hover:bg-muted/80 dark:hover:bg-muted/30"
        };
    }
  }
} as const;

/**
 * Common Component Patterns
 */
export const components = {
  // Header
  header:
    "flex h-(--header-height) shrink-0 items-center gap-2 border-b transition-[width,height] ease-linear group-has-data-[collapsible=icon]/sidebar-wrapper:h-(--header-height)",
  headerContent: "flex w-full items-center gap-1 px-4 lg:gap-2 lg:px-6",
  headerActions: "ml-auto flex items-center gap-2",

  // Sidebar
  sidebar:
    "bg-sidebar text-sidebar-foreground w-(--sidebar-width) p-0 [&>button]:hidden",
  sidebarContent: "flex h-full w-full flex-col",
  sidebarGroup: "flex flex-col gap-2 p-2",
  sidebarMenu: "flex flex-col gap-2 p-2",
  sidebarMenuItem: "group/menu-item relative",
  sidebarMenuButton: "flex w-full min-w-0 flex-col p-2",
  sidebarMenuAction: "flex h-8 items-center gap-2 rounded-md px-2",
  sidebarWrapper:
    "group/sidebar-wrapper has-data-[variant=inset]:bg-sidebar flex min-h-svh w-full",
  sidebarContainer:
    "fixed inset-y-0 z-10 hidden h-svh w-(--sidebar-width) transition-[left,right,width] duration-200 ease-linear md:flex",
  sidebarLeft:
    "left-0 group-data-[collapsible=offcanvas]:left-[calc(var(--sidebar-width)*-1)]",
  sidebarRight:
    "right-0 group-data-[collapsible=offcanvas]:right-[calc(var(--sidebar-width)*-1)]",
  sidebarFloating:
    "p-2 group-data-[collapsible=icon]:w-[calc(var(--sidebar-width-icon)+(--spacing(4))+2px)]",
  sidebarGroupContent: "w-full text-sm",

  // Cards (default surface — keep aligned with `cardVariants` default in `components/ui/card-variants.ts`)
  card: "bg-card text-card-foreground flex flex-col gap-6 rounded-xl border py-6 shadow-sm",
  cardHeader:
    "@container/card-header grid auto-rows-min grid-rows-[auto_auto] items-start gap-1.5 px-6 has-data-[slot=card-action]:grid-cols-[1fr_auto] [.border-b]:pb-6",
  cardTitle: "leading-none font-semibold",
  cardDescription: "text-muted-foreground text-sm",
  cardContent: "px-6",
  cardAction: "col-start-2 row-span-2 row-start-1 self-start justify-self-end",
  cardFooter: "flex items-center px-6 [.border-t]:pt-6",
  cardGrid:
    "*:data-[slot=card]:from-primary/5 *:data-[slot=card]:to-card dark:*:data-[slot=card]:bg-card grid grid-cols-1 gap-4 px-4 *:data-[slot=card]:bg-gradient-to-t *:data-[slot=card]:shadow-xs lg:px-6 @xl/main:grid-cols-2 @5xl/main:grid-cols-4",

  // Page layouts
  pageContainer: "flex flex-col gap-1 md:gap-2",
  pageHeader: "px-4 lg:px-6",
  pageContent: "px-4 lg:px-6",
  pageContentContainer: "flex flex-col gap-4 md:gap-6",

  // Form elements
  input:
    "file:text-foreground placeholder:text-muted-foreground selection:bg-primary selection:text-primary-foreground dark:bg-input/30 border-input flex h-9 w-full min-w-0 rounded-md border bg-background px-3 py-1.5 text-sm shadow-xs transition-[color,box-shadow] outline-none file:inline-flex file:h-7 file:border-0 file:bg-transparent file:text-sm file:font-medium disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive",
  label:
    "flex items-center gap-1 text-sm leading-none font-medium select-none group-data-[disabled=true]:pointer-events-none group-data-[disabled=true]:opacity-50 peer-disabled:cursor-not-allowed peer-disabled:opacity-50",

  // Buttons
  button:
    "inline-flex items-center justify-center whitespace-nowrap rounded-lg text-sm font-medium transition-colors disabled:pointer-events-none disabled:opacity-50",
  buttonIcon: "size-8",
  buttonGhost: "hidden sm:flex",
  buttonPrimary:
    "bg-primary text-primary-foreground hover:bg-primary/90 active:bg-primary/90 disabled:bg-secondary disabled:text-primary-foreground",
  buttonSecondary:
    "bg-secondary text-secondary-foreground hover:bg-secondary/90",
  buttonDestructive: "bg-destructive text-white hover:bg-destructive/90",
  buttonOutline:
    "text-primary border border-primary bg-background hover:bg-accent disabled:border disabled:border-muted-foreground font-normal disabled:bg-primary-foreground disabled:text-muted-foreground",
  buttonIconSquare:
    "inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:pointer-events-none disabled:opacity-50 hover:bg-accent hover:text-accent-foreground h-9 px-3",
  buttonIconSquareIcon: "h-4 w-4",
  buttonMedium:
    "h-13 w-full border-[1.2px] border-disabled bg-primary text-primary-foreground rounded-md text-base uppercase disabled:bg-background disabled:text-disabled dark:bg-primary cursor-pointer",
  buttonSmall:
    "h-10 w-full border-[1.2px] border-disabled bg-primary text-primary-foreground rounded-md text-sm uppercase disabled:bg-background disabled:text-disabled dark:bg-primary cursor-pointer",
  buttonContinue: "px-8 bg-primary hover:bg-primary/90 text-primary-foreground",
  buttonGreen:
    "text-green-600 hover:text-green-700 hover:bg-green-50 dark:hover:bg-green-950/50",

  // Dropdowns
  dropdownContent: "w-24 rounded-lg",
  dropdownItem: "flex items-center gap-2",
  dropdownSeparator: "bg-border pointer-events-none -mx-1 my-1 h-px",
  dropdownTrigger:
    "w-(--radix-dropdown-menu-trigger-width) min-w-56 rounded-lg",

  // Tooltips
  tooltipArrow:
    "bg-primary fill-primary z-50 size-2.5 translate-y-[calc(-50%_-_2px)] rotate-45 rounded-[2px]",

  // Tables
  tableContainer: "relative w-full overflow-x-auto",
  table: "w-full caption-bottom text-sm",
  tableHeader: "[&_tr]:border-b",
  tableBody: "[&_tr:last-child]:border-0",
  tableRow:
    "hover:bg-muted/50 data-[state=selected]:bg-muted border-b transition-colors",
  tableCaption: "text-muted-foreground mt-4 text-sm",

  // Tabs
  tabsList:
    "bg-muted text-muted-foreground inline-flex h-9 w-fit items-center justify-center rounded-lg p-[3px]",
  tabsTrigger:
    "data-[state=active]:bg-background dark:data-[state=active]:text-foreground focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:outline-ring dark:data-[state=active]:border-input dark:data-[state=active]:bg-input/30 text-foreground dark:text-muted-foreground inline-flex h-[calc(100%-1px)] flex-1 items-center justify-center gap-1.5 rounded-md border border-transparent px-2 py-1 text-sm font-medium whitespace-nowrap transition-[color,box-shadow] focus-visible:ring-[3px] focus-visible:outline-1 disabled:pointer-events-none disabled:opacity-50 data-[state=active]:shadow-sm [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",

  // Separators
  separatorVertical: "mx-2 data-[orientation=vertical]:h-4",

  // Toaster
  toaster: "toaster group",

  // Skeleton
  skeleton: "bg-accent animate-pulse rounded-md",

  // Sheet
  sheetOverlay:
    "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-50 bg-black/50",
  sheetContent:
    "bg-background data-[state=open]:animate-in data-[state=closed]:animate-out fixed z-50 flex flex-col gap-4 shadow-lg transition ease-in-out data-[state=closed]:duration-300 data-[state=open]:duration-500",
  sheetContentRight:
    "data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right inset-y-0 right-0 h-full w-3/4 border-l sm:max-w-sm",
  sheetContentLeft:
    "data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left inset-y-0 left-0 h-full w-3/4 border-r sm:max-w-sm",
  sheetContentTop:
    "data-[state=closed]:slide-out-to-top data-[state=open]:slide-in-from-top inset-x-0 top-0 h-auto border-b",
  sheetContentBottom:
    "data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom inset-x-0 bottom-0 h-auto border-t",
  sheetClose:
    "data-[state=open]:bg-secondary absolute top-4 right-4 rounded-xs opacity-70 transition-opacity hover:opacity-100 outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:pointer-events-none",
  sheetHeader: "flex flex-col gap-1.5 p-4",
  sheetTitle: "text-foreground font-semibold",

  // Badge
  // badge: "inline-flex items-center justify-center rounded-md border px-2 py-0.5 text-xs font-medium w-fit whitespace-nowrap shrink-0 [&>svg]:size-3 gap-1 [&>svg]:pointer-events-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive transition-[color,box-shadow] overflow-hidden",
  badge: cn(
    "w-fit flex items-center gap-[3px] rounded-sm pt-[2px] pr-[5px] pb-[3px] pl-[4px]",
    typography.textCaptionMedium12
  ),
  badgeDefault:
    "border-transparent bg-primary text-primary-foreground [a&]:hover:bg-primary/90",
  badgeSecondary:
    "border-transparent bg-secondary text-secondary-foreground [a&]:hover:bg-secondary/90",
  badgeDestructive:
    "border-transparent bg-destructive text-white [a&]:hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive/60",
  badgeOutline:
    "text-foreground [a&]:hover:bg-accent [a&]:hover:text-accent-foreground",

  // Breadcrumb
  breadcrumbList:
    "text-muted-foreground flex flex-wrap items-center gap-1 text-sm break-words",
  breadcrumbPage: "text-foreground font-normal",

  // Avatar
  avatar: "relative flex h-10 w-10 shrink-0 overflow-hidden rounded-full",
  avatarFallback:
    "flex h-full w-full items-center justify-center rounded-full bg-muted",

  // Checkbox
  checkbox:
    "peer h-4 w-4 shrink-0 rounded-sm border border-primary shadow-xs focus-visible:outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-50 data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive",
  checkboxIndicator:
    "flex items-center justify-center text-current transition-none",

  // Select
  select:
    "flex h-9 w-full items-center justify-between rounded-md border border-input bg-background dark:bg-input/30 px-3 py-1.5 text-sm shadow-xs placeholder:text-muted-foreground transition-[color,box-shadow] outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-50 [&>span]:line-clamp-1 aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive",
  selectTrigger:
    "flex h-9 w-full items-center justify-between rounded-md border border-input bg-background dark:bg-input/30 px-3 py-1.5 text-sm shadow-xs placeholder:text-muted-foreground transition-[color,box-shadow] outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-50 [&>span]:line-clamp-1 aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive",
  selectTriggerFormField:
    "w-full h-9 border-input bg-background dark:bg-input/30 px-3 py-1.5 text-sm shadow-xs transition-[color,box-shadow] outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50",
  selectContent:
    "relative z-50 min-w-[8rem] overflow-hidden rounded-md border bg-popover text-popover-foreground shadow-md data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
  selectItem:
    "relative flex w-full cursor-default select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-sm outline-none focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
  selectSeparator: "bg-border -mx-1 my-1 h-px",

  // Drawer
  drawerOverlay:
    "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-50 bg-black/50",
  drawerContent:
    "bg-background data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-50 flex flex-col",
  drawerHeader: "flex flex-col gap-1.5 p-4",
  drawerTitle: "text-foreground font-semibold",
  drawerDescription: "text-muted-foreground text-sm",
  drawerFooter: "mt-auto flex flex-col gap-2 p-4",

  // Chart
  chart: "w-full",
  chartLegend: "grid gap-1.5",
  chartLegendItem: "flex items-center gap-2",
  chartLegendLabel: "text-muted-foreground",
  chartLegendValue: "text-foreground font-mono font-medium tabular-nums",
  chartLegendColor: "h-2 w-2 shrink-0 rounded-[2px]",

  // Data Table
  dataTableRow:
    "relative z-0 data-[dragging=true]:z-10 data-[dragging=true]:opacity-80",
  dataTableCell:
    "hover:bg-input/30 focus-visible:bg-background dark:hover:bg-input/30 dark:focus-visible:bg-input/30 h-8 w-16 border-transparent bg-transparent text-right shadow-none focus-visible:border data-[state=open]:bg-background",

  // Navigation
  navGroup: "group-data-[collapsible=icon]:hidden",
  navButton:
    "data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground",
  navDropdown: "w-(--radix-dropdown-menu-trigger-width) min-w-56 rounded-lg",
  navDropdownLabel: "p-0 font-normal",
  navDropdownItem: "flex items-center gap-2 px-1 py-1.5 text-left text-sm",

  // Mode Toggle
  modeToggleSun:
    "h-[1.2rem] w-[1.2rem] scale-100 rotate-0 transition-all dark:scale-0 dark:-rotate-90",
  modeToggleMoon:
    "absolute h-[1.2rem] w-[1.2rem] scale-0 rotate-90 transition-all dark:scale-100 dark:rotate-0",

  // Login Form
  loginForm: "flex flex-col gap-6",
  loginFormHeader: "flex flex-col items-center gap-2 text-center",
  loginFormTitle: "text-2xl font-bold",
  loginFormSubtitle: "text-muted-foreground text-sm text-balance",
  loginFormGrid: "grid gap-6",
  loginFormField: "grid gap-3",
  loginFormFieldRow: "flex items-center",
  loginFormLink: "ml-auto text-sm underline-offset-4 hover:underline",
  loginFormButton: cn(
    layout.flexCenterGap2,
    spacing.px4,
    spacing.py2,
    "h-10",
    typography.textSm
  ),
  loginFormProviderButton: cn(
    layout.flexCenterGap2,
    spacing.px4,
    spacing.py2,
    "h-10",
    typography.textSm,
    "justify-center"
  ),
  loginFormDivider:
    "after:border-border relative text-center text-sm after:absolute after:inset-0 after:top-1/2 after:z-0 after:flex after:items-center after:border-t",
  loginFormDividerText:
    "bg-background text-muted-foreground relative z-10 px-2",
  loginFormFooter: "text-center text-sm",
  loginFormFooterLink: "underline underline-offset-4",

  // Login Page
  loginPage: "grid min-h-svh lg:grid-cols-2",
  loginPageLeft: "flex flex-col gap-4 p-6 md:p-10",
  loginPageBrand: "flex justify-center gap-2 md:justify-start",
  loginPageBrandLink: "flex items-center gap-2 font-medium",
  loginPageBrandIcon:
    "bg-primary text-primary-foreground flex size-6 items-center justify-center rounded-md",
  loginPageContent: "flex flex-1 items-center justify-center",
  loginPageFormWrapper: "w-full max-w-xs",
  loginPageRight: "bg-muted relative hidden lg:block",
  loginPageImage:
    "absolute inset-0 h-full w-full object-cover dark:brightness-[0.2] dark:grayscale",

  uploadField:
    "relative flex flex-col items-center justify-center border-2 border-border border-dashed rounded-lg px-3 py-2 min-h-[91px]",
  uploadedFile:
    "relative flex flex-col items-center justify-center border border-border rounded-lg px-3 py-2 min-h-[91px]",

  // progress bar
  progressBarContainer: "w-full bg-[#7A8FA61F] rounded h-1 mt-1",
  progressBar:
    "bg-linear-to-r from-[#064BB5] to-[#49AEE2] h-1 rounded transition-all duration-200",

  // divider
  divider: "flex-1 h-px border-t border-divider/30",

  // chip active:bg-[#49AEE226] active:border-4
  chip: cn(
    "h-9 bg-[#7A8FA612] p-2 px-3.5 !rounded-lg bg-[#7A8FA612] flex items-center justify-center gap-2.5 cursor-pointer data-[state=on]:bg-[#49AEE226] data-[state=on]:border data-[state=on]:!border-[#064BB5] group"
    // typography.textSmallMedium14
  ),
  chipIcon:
    "size-5 rotate-45 text-disabled group-data-[state=on]:rotate-0 group-data-[state=on]:text-[#064BB5] transition"
} as const;

/**
 * Utility Classes for Common Patterns
 */
export const utils = {
  // Common flex patterns
  flexCenterGap2: cn(layout.flexCenterGap2),
  flexCenterGap3: cn(layout.flexCenterGap3),
  flexCenterGap4: cn(layout.flexCenterGap4),
  flexBetweenGap2: cn(layout.flexBetweenGap2),
  flexColGap4: cn(layout.flexColGap4),
  flexColGap6: cn(layout.flexColGap6),

  // Common padding patterns
  p4LgP6: cn(spacing.p4LgP6),
  py4MdPy6: cn(spacing.py4MdPy6),
  gap4MdGap6: cn(spacing.gap4MdGap6),

  // Common text patterns
  text3xlBold: cn(typography.text3xlBold),
  text2xlSemibold: cn(typography.text2xlSemibold),
  textBaseMedium: cn(typography.textBaseMedium),
  textSmMuted: cn(typography.textSmMuted),
  textXsMuted: cn(typography.textXsMuted),
  textMuted: cn(typography.textMuted),
  textForegroundSemibold: cn(typography.textForegroundSemibold),
  textSidebarForeground70: cn(typography.textSidebarForeground70),
  text4xlBold: cn(typography.text4xlBold),
  text6xlBold: cn(typography.text6xlBold),
  textXlSemibold: cn(typography.textXlSemibold),
  textLgSemibold: cn(typography.textLgSemibold),
  textLgBold: cn(typography.textLgBold),
  text4xlMdText6xl: cn(typography.text4xlMdText6xl),
  srOnly: cn(typography.srOnly),

  // Common layout patterns
  pageLayout: cn(
    layout.container,
    layout.containerMain,
    components.pageContainer
  ),
  cardLayout: cn(layout.grid, components.cardGrid),

  // Common button patterns
  buttonGhostSm: cn(components.buttonGhost),

  // Common header patterns
  headerLayout: cn(components.header),
  headerContent: cn(components.headerContent),
  headerActions: cn(components.headerActions),

  // Common form patterns
  formInput: cn(components.input),
  formLabel: cn(components.label),

  // Common table patterns
  tableContainer: cn(components.tableContainer),
  tableRow: cn(components.tableRow),

  // Common dropdown patterns
  dropdownContent: cn(components.dropdownContent),
  dropdownSeparator: cn(components.dropdownSeparator),

  // Common badge patterns
  badgeOutline: cn(components.badge, components.badgeOutline),
  badgeMuted: cn(components.badge, "text-muted-foreground px-1.5"),

  // Common avatar patterns
  avatar: cn(components.avatar),
  avatarFallback: cn(components.avatarFallback),

  // Common skeleton patterns
  skeleton: cn(components.skeleton),

  // Common navigation patterns
  navGroup: cn(components.navGroup),
  navButton: cn(components.navButton),

  // Common mode toggle patterns
  modeToggleSun: cn(components.modeToggleSun),
  modeToggleMoon: cn(components.modeToggleMoon),

  // Common login patterns
  loginFormContainer: cn(components.loginForm),
  loginFormHeader: cn(components.loginFormHeader),
  loginFormTitle: cn(components.loginFormTitle),
  loginFormSubtitle: cn(components.loginFormSubtitle),
  loginFormGrid: cn(components.loginFormGrid),
  loginFormField: cn(components.loginFormField),
  loginFormFieldRow: cn(components.loginFormFieldRow),
  loginFormLink: cn(components.loginFormLink),
  loginFormButton: cn(components.loginFormButton),
  loginFormProviderButton: cn(components.loginFormProviderButton),
  loginFormDivider: cn(components.loginFormDivider),
  loginFormDividerText: cn(components.loginFormDividerText),
  loginFormFooter: cn(components.loginFormFooter),
  loginFormFooterLink: cn(components.loginFormFooterLink),

  // Common login page patterns
  loginPageContainer: cn(components.loginPage),
  loginPageLeft: cn(components.loginPageLeft),
  loginPageBrand: cn(components.loginPageBrand),
  loginPageBrandLink: cn(components.loginPageBrandLink),
  loginPageBrandIcon: cn(components.loginPageBrandIcon),
  loginPageContent: cn(components.loginPageContent),
  loginPageFormWrapper: cn(components.loginPageFormWrapper),
  loginPageRight: cn(components.loginPageRight),
  loginPageImage: cn(components.loginPageImage)
} as const;

/**
 * Z-Index Utilities
 */
export const zIndex = {
  z0: "z-0",
  z10: "z-10",
  z50: "z-50"
} as const;

/**
 * Export all utilities as a single object for easy access
 */
export const tailwind = {
  layout,
  spacing,
  typography,
  borders,
  backgrounds,
  sizes,
  position,
  interactive,
  animation,
  shadows,
  zIndex,
  opacity,
  overflow,
  verification,
  components,
  utils,
  cn
} as const;

export default tailwind;
